import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Users, Settings, Menu, X, LogOut, 
  UserCog, Shield, ChevronRight, Bell, Search, ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button"; 
import { Badge } from "@/components/ui/badge";   
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"; 

// [FIX] Use relative path to be safe in the Lab environment
import { useAuth } from "../contexts/AuthContext";

// [FIX] Simple utility for conditional classes
const cls = (...classes) => classes.filter(Boolean).join(" ");

// --- CONFIGURATION ---
const getNavItems = (isAdmin) => {
  const items = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/users", label: "User Management", icon: Users },
  ];
  if (isAdmin) {
    items.push({ path: "/admin-users", label: "Administrators", icon: ShieldCheck });
    items.push({ path: "/settings", label: "Router Settings", icon: Settings });
  }
  return items;
};

const ROLE_COLORS = {
  admin: "bg-emerald-100 text-emerald-800 border-emerald-200",
  operator: "bg-blue-100 text-blue-800 border-blue-200",
  viewer: "bg-slate-100 text-slate-800 border-slate-200",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  // STATE
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const navItems = getNavItems(isAdmin);

  // Close mobile menu on route change
  useEffect(() => setIsMobileOpen(false), [location.pathname]);

  const getCurrentPageTitle = () => navItems.find(n => location.pathname.startsWith(n.path))?.label || "Dashboard";
  const username = user?.username || "Guest";
  const role = user?.role || "Viewer";
  const initial = username[0]?.toUpperCase() || "U";

  return (
    // [FIX] Changed 'lg:' to 'md:' so sidebar appears on smaller Preview screens
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      
      {/* --- 1. MOBILE HEADER (Visible only on very small screens) --- */}
      <header className="md:hidden sticky top-0 z-40 bg-[#1a3d2e] text-white p-4 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
           <Button variant="ghost" size="icon" onClick={() => setIsMobileOpen(true)} className="text-white hover:bg-white/10 -ml-2">
             <Menu className="w-6 h-6" />
           </Button>
           <span className="font-bold text-lg tracking-tight">TAU Hotspot</span>
        </div>
        <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center font-bold text-sm border border-white/20 shadow-sm">
          {initial}
        </div>
      </header>

      {/* --- 2. SIDEBAR (Green Rail) --- */}
      <aside
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cls(
          // [FIX] z-[100] ensures it stays on top. 'md:' classes force desktop view earlier.
          "fixed inset-y-0 left-0 z-[100] bg-gradient-to-b from-[#1a3d2e] to-[#2e5c44] text-white shadow-2xl transition-all duration-300 ease-in-out flex flex-col",
          // Mobile Logic
          isMobileOpen ? "translate-x-0 w-72" : "-translate-x-full md:translate-x-0",
          // Desktop Hover Logic (Collapsed vs Expanded)
          isHovered ? "md:w-72" : "md:w-20"
        )}
      >
        {/* Mobile Close Button */}
        <div className="md:hidden absolute top-4 right-4">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10">
            <X className="w-6 h-6" />
          </Button>
        </div>

        {/* LOGO AREA */}
        <div className="h-20 flex items-center px-4 overflow-hidden border-b border-white/10 shrink-0">
           <div className="flex items-center gap-4 min-w-max transition-all duration-300">
              <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center p-1 shadow-lg border-2 border-emerald-500/20 mx-auto md:mx-0.5">
                 <img src="/tau-logo.png" alt="TAU" className="w-full h-full object-contain" onError={(e) => e.target.style.display='none'} />
              </div>
              <div className={cls("transition-opacity duration-300", !isHovered ? "md:opacity-0 md:w-0 md:hidden" : "opacity-100")}>
                 <h1 className="font-bold text-lg leading-none">TAU Hotspot</h1>
                 <p className="text-[10px] text-emerald-200 uppercase tracking-wider mt-0.5">Admin Console</p>
              </div>
           </div>
        </div>

        {/* NAVIGATION LINKS */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto overflow-x-hidden">
           {navItems.map(item => (
             <NavLink
               key={item.path}
               to={item.path}
               className={({ isActive }) => cls(
                 "flex items-center gap-4 px-3.5 py-3 rounded-xl transition-all duration-200 group whitespace-nowrap relative",
                 isActive 
                   ? "bg-white text-[#1a3d2e] shadow-md font-bold" 
                   : "text-emerald-100 hover:bg-white/10 hover:text-white"
               )}
             >
               <item.icon className="w-5 h-5 flex-shrink-0" />
               <span className={cls("transition-opacity duration-300", !isHovered ? "md:opacity-0 md:w-0 md:hidden" : "opacity-100")}>
                 {item.label}
               </span>
               
               {/* Hover Tooltip (Only when collapsed on Desktop) */}
               {!isHovered && (
                 <div className="hidden md:block absolute left-16 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl ml-2 whitespace-nowrap">
                   {item.label}
                 </div>
               )}
             </NavLink>
           ))}
        </nav>

        {/* USER PROFILE FOOTER */}
        <div className="p-4 border-t border-white/10 bg-[#153326]/50 shrink-0">
           <div className="flex items-center gap-3 overflow-hidden whitespace-nowrap">
              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center font-bold text-sm shadow-md border border-white/20 shrink-0">
                {initial}
              </div>
              
              <div className={cls("flex-1 min-w-0 transition-opacity duration-300", !isHovered ? "md:opacity-0 md:w-0 md:hidden" : "opacity-100")}>
                 <p className="font-bold text-sm truncate">{username}</p>
                 <p className="text-xs text-emerald-300 capitalize flex items-center gap-1">
                   <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span> {role}
                 </p>
              </div>

              <button 
                onClick={() => { logout(); navigate("/login"); }} 
                className={cls("text-emerald-300 hover:text-red-300 hover:bg-red-500/20 p-2 rounded-lg transition-colors", !isHovered && "md:hidden")}
                title="Logout"
              >
                 <LogOut className="w-5 h-5" />
              </button>
           </div>
        </div>
      </aside>

      {/* --- 3. MOBILE OVERLAY --- */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden backdrop-blur-sm transition-opacity" onClick={() => setIsMobileOpen(false)} />
      )}

      {/* --- 4. MAIN CONTENT AREA --- */}
      <main className={cls(
        "flex-1 transition-all duration-300 flex flex-col min-h-screen w-full",
        // [FIX] Adjusted margins for 'md' breakpoint
        isHovered ? "md:ml-72" : "md:ml-20"
      )}>
         
         {/* DESKTOP HEADER */}
         <header className="hidden md:flex h-20 bg-white/80 backdrop-blur-xl border-b border-slate-200 items-center justify-between px-8 sticky top-0 z-30 shadow-sm">
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">{getCurrentPageTitle()}</h1>
            
            <div className="flex items-center gap-6">
               <div className="flex flex-col items-end">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</span>
                  <span className="text-xs font-bold text-emerald-600 flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    Operational
                  </span>
               </div>
               
               <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="rounded-full w-10 h-10 p-0 border border-slate-200 hover:bg-slate-100">
                       <span className="font-bold text-emerald-700">{initial}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 mt-2">
                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="cursor-default">
                      <Badge className={cls("mr-2", ROLE_COLORS[role] || ROLE_COLORS.viewer)}>{role}</Badge> Role
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} className="text-red-600 focus:bg-red-50 cursor-pointer">
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
               </DropdownMenu>
            </div>
         </header>

         {/* PAGE CONTENT */}
         <div className="p-4 lg:p-8 flex-1 w-full max-w-[1920px] mx-auto overflow-x-hidden">
            <Outlet />
         </div>
      </main>

    </div>
  );
}